(() => {
  'use strict';
  if (customElements.get('up-announcement-bar')) return;

  class UPAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      try {
        this.config = JSON.parse(this.querySelector('[data-up-announcement-config]')?.textContent || '{}');
      } catch (_) {
        return;
      }
      this.carousel = this.querySelector('.up-announcement-carousel');
      this.track = this.querySelector('[data-up-marquee-track]');
      this.group = this.track?.querySelector('[data-up-marquee-group]');
      this.slides = [...(this.group?.querySelectorAll('[data-up-slide]') || [])];
      this.toggle = this.querySelector('[data-up-toggle]');
      this.shipping = this.querySelector('[data-up-shipping]');
      this.shippingMessage = this.querySelector('[data-up-shipping-message]');
      this.progress = this.querySelector('[data-up-progress]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.paused === true;
      this.hovered = false;
      this.focused = !!this.carousel?.contains(document.activeElement);
      this.editorSelected = false;
      this.revision = this.revision || 0;
      this.cartSignature = null;
      this.lastSync = Date.now();
      const speed = Number(this.dataset.scrollSpeed);
      this.speed = Number.isFinite(speed) && speed > 0 ? speed : 35;
      const language = document.documentElement.lang || 'es';
      this.locale = language.toLowerCase().startsWith('es') ? 'es-MX' : language;
      this.labels = this.config.labels || {};

      if (this.carousel && this.track && this.group && this.slides.length) {
        this.slides.forEach(slide => {
          slide.hidden = false;
          slide.removeAttribute('aria-hidden');
        });
        this.createCopy();
        if (this.toggle) this.toggle.hidden = false;
        this.toggle?.addEventListener('pointerdown', () => { this.playbackIntent = !this.paused; }, options);
        this.toggle?.addEventListener('pointercancel', () => { this.playbackIntent = undefined; }, options);
        this.toggle?.addEventListener('click', () => {
          this.paused = typeof this.playbackIntent === 'boolean' ? this.playbackIntent : !this.paused;
          this.playbackIntent = undefined;
          this.updatePlayback();
        }, options);
        this.carousel.addEventListener('focusin', event => {
          if (this.copy?.contains(event.target) || !event.target.matches(':focus-visible')) return;
          this.focused = true;
          this.setStaticMode();
        }, options);
        this.carousel.addEventListener('focusout', event => {
          if (this.carousel.contains(event.relatedTarget)) return;
          this.focused = false;
          this.queueMarquee();
        }, options);
        this.carousel.addEventListener('pointerenter', event => {
          if (event.pointerType === 'touch') return;
          this.hovered = true;
          this.syncMotion();
        }, options);
        this.carousel.addEventListener('pointerleave', event => {
          if (event.pointerType === 'touch') return;
          this.hovered = false;
          this.syncMotion();
        }, options);
        this.motion.addEventListener('change', () => this.queueMarquee(), options);
        this.addEventListener('shopify:block:select', event => {
          const selected = this.slides.some(slide => slide.dataset.upBlockId === event.detail?.blockId || slide.contains(event.target));
          if (!selected) return;
          this.editorSelected = true;
          this.setStaticMode();
        }, options);
        this.addEventListener('shopify:block:deselect', () => {
          this.editorSelected = false;
          this.queueMarquee();
        }, options);
        if (typeof ResizeObserver !== 'undefined') {
          this.marqueeObserver = new ResizeObserver(() => this.queueMarquee());
          this.marqueeObserver.observe(this.carousel);
          this.marqueeObserver.observe(this.group);
        } else {
          window.addEventListener('resize', () => this.queueMarquee(), options);
        }
        document.fonts?.ready.then(() => {
          if (!options.signal.aborted && this.isConnected) this.queueMarquee();
        });
      } else {
        this.setStaticMode();
      }

      this.updatePlayback();
      if (this.config.shippingEnabled && Number(this.config.threshold) > 0) {
        this.renderCart(this.config.cart, false);
        document.addEventListener('cart:item-added', () => this.queueCart(), options);
        document.addEventListener('line-item:change:end', event => {
          if (this.isCart(event.detail?.cart)) this.acceptCart(event.detail.cart);
          else this.queueCart();
        }, options);
        document.addEventListener('cart:refresh', () => this.queueCart(), options);
        document.addEventListener('cart-drawer:open', () => {
          if (Date.now() - this.lastSync > 15000) this.queueCart();
        }, options);
        window.addEventListener('pageshow', event => {
          if (event.persisted) this.queueCart();
        }, options);
      }
      document.addEventListener('visibilitychange', () => {
        this.syncMotion();
        if (!document.hidden && this.shipping && Date.now() - this.lastSync > 30000) this.queueCart();
      }, options);

      this.section = this.closest('.announcement-bar-section');
      if (this.section && typeof ResizeObserver !== 'undefined') {
        this.heightObserver = new ResizeObserver(() => this.syncHeight());
        this.heightObserver.observe(this.section);
      }
      this.syncHeight();
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      this.events?.abort();
      this.request?.abort();
      this.heightObserver?.disconnect();
      this.marqueeObserver?.disconnect();
      if (this.marqueeFrame !== undefined) cancelAnimationFrame(this.marqueeFrame);
      this.marqueeFrame = undefined;
      this.rememberPhase();
      this.marqueeAnimation?.cancel();
      this.marqueeAnimation = null;
      this.copy?.remove();
      this.copy = null;
      clearTimeout(this.cartTimer);
      this.revision = (this.revision || 0) + 1;
    }

    syncHeight() {
      if (!this.isConnected || !this.section) return;
      const height = Math.ceil(this.section.getBoundingClientRect().height);
      if (height === this.lastHeight) return;
      this.lastHeight = height;
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
      window.dispatchEvent(new Event('scroll'));
    }

    createCopy() {
      this.track.querySelectorAll('[data-up-marquee-copy]').forEach(copy => copy.remove());
      this.copy = this.group.cloneNode(true);
      this.copy.removeAttribute('data-up-marquee-group');
      this.copy.setAttribute('data-up-marquee-copy', '');
      this.copy.setAttribute('aria-hidden', 'true');
      [this.copy, ...this.copy.querySelectorAll('*')].forEach(element => {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('data-up-block-id');
        element.removeAttribute('data-up-marquee-group');
      });
      this.copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(element => element.setAttribute('tabindex', '-1'));
      this.copy.hidden = true;
      this.track.append(this.copy);
    }

    updatePlayback() {
      this.toggle?.setAttribute('aria-pressed', String(this.paused));
      this.toggle?.setAttribute('aria-label', this.paused ? this.labels.play || '' : this.labels.pause || '');
      this.syncMotion();
      this.queueMarquee();
    }

    queueMarquee() {
      if (!this.isConnected || this.marqueeFrame !== undefined) return;
      this.marqueeFrame = requestAnimationFrame(() => {
        this.marqueeFrame = undefined;
        this.measureMarquee();
      });
    }

    rememberPhase() {
      const time = Number(this.marqueeAnimation?.currentTime);
      if (this.marqueeAnimation && this.marqueeDuration > 0 && Number.isFinite(time)) {
        this.marqueePhase = ((time / this.marqueeDuration) % 1 + 1) % 1;
      }
    }

    setStaticMode() {
      this.rememberPhase();
      this.marqueeAnimation?.cancel();
      this.marqueeAnimation = null;
      this.carousel?.classList.add('is-marquee-static');
      if (this.copy) this.copy.hidden = true;
    }

    measureMarquee() {
      if (!this.isConnected) return;
      if (!this.carousel || !this.track || !this.group || !this.copy || !this.slides.length || typeof this.track.animate !== 'function'
        || this.dataset.autoplay !== 'true' || this.motion.matches || this.focused || this.editorSelected) {
        this.setStaticMode();
        return;
      }
      const viewportWidth = this.carousel.clientWidth;
      if (!(viewportWidth > 0)) {
        this.setStaticMode();
        return;
      }
      this.carousel.classList.remove('is-marquee-static');
      if (viewportWidth !== this.marqueeViewportWidth) {
        this.marqueeViewportWidth = viewportWidth;
        this.carousel.style.setProperty('--up-marquee-viewport', `${viewportWidth}px`);
      }
      this.copy.hidden = false;
      const width = this.group.getBoundingClientRect().width;
      if (!(width > 0) || !Number.isFinite(width)) {
        this.setStaticMode();
        return;
      }
      const duration = width / this.speed * 1000;
      if (this.marqueeAnimation && Math.abs(width - this.marqueeWidth) < .1 && Math.abs(duration - this.marqueeDuration) < 1) {
        this.syncMotion();
        return;
      }
      this.rememberPhase();
      this.marqueeAnimation?.cancel();
      this.marqueeWidth = width;
      this.marqueeDuration = duration;
      this.marqueeAnimation = this.track.animate([
        { transform: 'translate3d(0, 0, 0)' },
        { transform: `translate3d(${-width}px, 0, 0)` }
      ], { duration, iterations: Infinity, easing: 'linear' });
      this.marqueeAnimation.currentTime = (this.marqueePhase || 0) * duration;
      this.syncMotion();
    }

    syncMotion() {
      if (!this.marqueeAnimation) return;
      if (this.paused || this.hovered || document.hidden || this.focused || this.editorSelected) this.marqueeAnimation.pause();
      else this.marqueeAnimation.play();
    }

    isCart(cart) {
      return !!cart && typeof cart.total_price === 'number' && Number.isFinite(cart.total_price)
        && cart.total_price >= 0 && typeof cart.currency === 'string';
    }

    cancelCartRequest() {
      clearTimeout(this.cartTimer);
      this.cartTimer = null;
      this.request?.abort();
      this.request = null;
      this.revision += 1;
    }

    acceptCart(cart) {
      this.cancelCartRequest();
      this.lastSync = Date.now();
      this.renderCart(cart, true);
    }

    queueCart() {
      if (!this.shipping || !this.config.shippingEnabled) return;
      this.cancelCartRequest();
      const revision = this.revision;
      this.cartTimer = setTimeout(() => this.fetchCart(revision), 125);
    }

    async fetchCart(revision) {
      this.cartTimer = null;
      if (!this.isConnected || revision !== this.revision) return;
      const request = new AbortController();
      this.request = request;
      try {
        const response = await fetch(this.config.cartUrl, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: request.signal
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (!this.isConnected || revision !== this.revision || !this.isCart(cart)) return;
        this.lastSync = Date.now();
        this.renderCart(cart, true);
      } catch (_) {
      } finally {
        if (this.request === request) this.request = null;
      }
    }

    money(cents, currency = this.config.currency) {
      const fractionDigits = cents % 100 === 0 ? 0 : 2;
      try {
        return `${new Intl.NumberFormat(this.locale, {
          style: 'currency', currency, currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits
        }).format(cents / 100)} ${currency}`;
      } catch (_) {
        return `$${(cents / 100).toFixed(fractionDigits)} ${currency}`;
      }
    }

    renderCart(cart, announce) {
      if (!this.shipping || !this.progress || !this.shippingMessage || !this.isCart(cart)) return;
      const threshold = Number(this.config.threshold);
      const supportedMarket = (this.config.country === 'MX' && this.config.currency === 'MXN')
        || (this.config.country === 'US' && this.config.currency === 'USD');
      const eligible = this.config.shippingEnabled && supportedMarket
        && cart.currency === this.config.currency && Number.isFinite(threshold) && threshold > 0;
      this.shipping.hidden = !eligible;
      this.classList.toggle('up-announcement--with-shipping', eligible);
      if (!eligible) return;
      const total = Math.max(0, cart.total_price);
      const remaining = Math.max(0, threshold - total);
      const current = Math.min(total, threshold);
      const complete = remaining === 0;
      const signature = `${total}:${threshold}:${cart.currency}`;
      if (signature === this.cartSignature) return;
      this.cartSignature = signature;
      const amount = this.money(remaining);
      const text = complete ? this.labels.achieved : (this.labels.remaining || '').replaceAll('__UP_AMOUNT__', amount);
      this.shippingMessage.setAttribute('aria-live', announce ? 'polite' : 'off');
      if (!announce) queueMicrotask(() => {
        if (this.isConnected) this.shippingMessage.setAttribute('aria-live', 'polite');
      });
      if (complete) {
        this.shippingMessage.textContent = text;
      } else {
        const parts = (this.labels.remaining || '').split('__UP_AMOUNT__');
        const content = document.createDocumentFragment();
        parts.forEach((part, index) => {
          if (index > 0) {
            const emphasis = document.createElement('strong');
            emphasis.textContent = amount;
            content.append(emphasis);
          }
          content.append(document.createTextNode(part));
        });
        this.shippingMessage.replaceChildren(content);
      }
      this.shipping.classList.toggle('is-complete', complete);
      this.progress.style.setProperty('--up-shipping-progress', `${Math.min(100, total / threshold * 100)}%`);
      this.progress.setAttribute('aria-valuemax', String(threshold));
      this.progress.setAttribute('aria-valuenow', String(current));
      this.progress.setAttribute('aria-valuetext', (this.labels.progressValue || '')
        .replaceAll('__UP_CURRENT__', this.money(total))
        .replaceAll('__UP_THRESHOLD__', this.money(threshold)));
    }
  }

  customElements.define('up-announcement-bar', UPAnnouncementBar);
})();
